import type { Component, TUI } from "@elyracode/tui";
import chalk from "chalk";

// ASCII representation of the Elyra favicon: a constellation of connected nodes
// with a bright star at the top (the north star / guiding light)
const LOGO_LINES = [
	"              *    ",
	"             /|\\   ",
	"            / | \\  ",
	"           /  |  \\ ",
	"          *   |   *",
	"          |\\  |  /|",
	"          | \\ | / |",
	"          |  \\|/  |",
	"          *---+---*",
];

const FRAME_INTERVAL_MS = 45;
const HOLD_MS = 600;

/**
 * Animated startup component that reveals the Elyra constellation logo
 * line by line with an orange gradient, holds briefly, then calls onComplete.
 */
export class StartupAnimation implements Component {
	private ui: TUI;
	private currentLine = 0;
	private phase: "reveal" | "hold" | "done" = "reveal";
	private intervalId: ReturnType<typeof setInterval> | undefined;
	private holdTimeoutId: ReturnType<typeof setTimeout> | undefined;
	private onComplete: () => void;
	private version: string;

	constructor(ui: TUI, version: string, onComplete: () => void) {
		this.ui = ui;
		this.version = version;
		this.onComplete = onComplete;
		this.start();
	}

	private start(): void {
		this.intervalId = setInterval(() => {
			if (this.phase === "reveal") {
				this.currentLine++;
				if (this.currentLine >= LOGO_LINES.length) {
					this.phase = "hold";
					this.clearInterval();
					this.holdTimeoutId = setTimeout(() => {
						this.phase = "done";
						this.onComplete();
					}, HOLD_MS);
				}
				this.ui.requestRender();
			}
		}, FRAME_INTERVAL_MS);
	}

	stop(): void {
		this.clearInterval();
		if (this.holdTimeoutId) {
			clearTimeout(this.holdTimeoutId);
			this.holdTimeoutId = undefined;
		}
		this.phase = "done";
	}

	invalidate(): void {
		// No cache to clear
	}

	private clearInterval(): void {
		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = undefined;
		}
	}

	render(_width: number): string[] {
		if (this.phase === "done") {
			return [];
		}

		const lines: string[] = [""];

		const visibleLines = LOGO_LINES.slice(0, this.currentLine);
		for (let i = 0; i < visibleLines.length; i++) {
			const line = visibleLines[i];
			// Orange gradient matching favicon: bright at top (star), deeper at bottom
			const t = visibleLines.length > 1 ? i / (visibleLines.length - 1) : 0;
			const r = Math.round(251 + t * (249 - 251));
			const g = Math.round(191 + t * (115 - 191));
			const b = Math.round(36 + t * (22 - 36));

			// Highlight nodes (*) brighter than lines
			const colored = line.replace(/./g, (ch) => {
				if (ch === "*" || ch === "+") {
					return chalk.rgb(251, 146, 60).bold(ch);
				}
				return chalk.rgb(r, g, b)(ch);
			});
			lines.push(`        ${colored}`);
		}

		if (this.phase === "hold") {
			lines.push("");
			const name = chalk.rgb(251, 146, 60).bold("elyra");
			const ver = chalk.dim(` v${this.version}`);
			lines.push(`        ${name}${ver}`);
		}

		lines.push("");
		return lines;
	}
}
